import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  refreshToken as apiRefreshToken,
  register as apiRegister,
  setActiveSociedadId as setApiActiveSociedadId,
  setAuthToken,
  setOnUnauthorized,
  type MeResponse,
  type RegisterPayload,
} from './api'

const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token'
const ACTIVE_SOCIEDAD_STORAGE_KEY = 'active_sociedad_id'

interface AuthContextValue {
  user: MeResponse | null
  accessToken: string | null
  activeSociedadId: string | null
  setActiveSociedadId: (id: string | null) => void
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  logout: () => Promise<void>
  /** Pide un access token fresco con el refresh token guardado, sin pedir
   * credenciales. Devuelve null (y cierra sesión) si el refresh ya no es válido —
   * usado por el reconnect de SSE cuando el access token vence a mitad de sesión. */
  refreshSession: () => Promise<string | null>
  /** Vuelve a pedir /auth/me sin tocar tokens — usado tras renombrar la
   * Sociedad activa para que el sidebar (SociedadSwitcher, badges de rango)
   * no quede mostrando el nombre viejo hasta el próximo login. */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Si el id guardado ya no es una Sociedad del usuario (cambió de cuenta, o el
// backend la revocó), cae a la primera — nunca a un id que no le pertenezca.
// null es un resultado válido: un SuperAdmin de plataforma puede no tener
// ninguna Sociedad propia (ver app/db/user.py::is_admin) — App.tsx lo manda
// directo a /admin en vez de intentar resolver una Sociedad que no existe.
function resolveActiveSociedadId(me: MeResponse, storedId: string | null): string | null {
  if (storedId && me.sociedades.some((s) => s.id === storedId)) return storedId
  return me.sociedades[0]?.id ?? null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [activeSociedadId, setActiveSociedadIdState] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setAuthToken(accessToken)
  }, [accessToken])

  function setActiveSociedadId(id: string | null): void {
    if (id) {
      localStorage.setItem(ACTIVE_SOCIEDAD_STORAGE_KEY, id)
    } else {
      localStorage.removeItem(ACTIVE_SOCIEDAD_STORAGE_KEY)
    }
    setApiActiveSociedadId(id)
    setActiveSociedadIdState(id)
  }

  async function applySession(token: string): Promise<void> {
    setAuthToken(token)
    const me = await fetchMe()
    const sociedadId = resolveActiveSociedadId(me, localStorage.getItem(ACTIVE_SOCIEDAD_STORAGE_KEY))
    setAccessToken(token)
    setUser(me)
    setActiveSociedadId(sociedadId)
  }

  function clearSession(): void {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(ACTIVE_SOCIEDAD_STORAGE_KEY)
    setAuthToken(null)
    setApiActiveSociedadId(null)
    setAccessToken(null)
    setActiveSociedadIdState(null)
    setUser(null)
  }

  useEffect(() => {
    setOnUnauthorized(clearSession)
  }, [])

  // Al montar: si hay un refresh token guardado (ej. se reabrió la pestaña
  // horas después), lo usamos para recuperar sesión sin pedir credenciales.
  // Guard con ref (no solo el array de deps []): en React StrictMode (dev)
  // este efecto se monta-desmonta-remonta a propósito, y sin el guard el
  // refresh token rotativo se consume dos veces — la segunda ve "reuso
  // detectado" y revoca la sesión recién creada.
  const bootstrapped = useRef(false)
  useEffect(() => {
    if (bootstrapped.current) return
    bootstrapped.current = true
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
    if (!storedRefreshToken) {
      setIsLoading(false)
      return
    }
    apiRefreshToken(storedRefreshToken)
      .then(async (tokens) => {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refresh_token)
        await applySession(tokens.access_token)
      })
      .catch(() => {
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
      })
      .finally(() => setIsLoading(false))
  }, [])

  async function login(email: string, password: string): Promise<void> {
    const tokens = await apiLogin(email, password)
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refresh_token)
    await applySession(tokens.access_token)
  }

  async function register(payload: RegisterPayload): Promise<void> {
    const tokens = await apiRegister(payload)
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refresh_token)
    await applySession(tokens.access_token)
  }

  async function logout(): Promise<void> {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
    if (storedRefreshToken) await apiLogout(storedRefreshToken)
    clearSession()
  }

  async function refreshSession(): Promise<string | null> {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
    if (!storedRefreshToken) {
      clearSession()
      return null
    }
    try {
      const tokens = await apiRefreshToken(storedRefreshToken)
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refresh_token)
      await applySession(tokens.access_token)
      return tokens.access_token
    } catch {
      clearSession()
      return null
    }
  }

  async function refreshUser(): Promise<void> {
    setUser(await fetchMe())
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        activeSociedadId,
        setActiveSociedadId,
        isLoading,
        login,
        register,
        logout,
        refreshSession,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  return ctx
}
